import { TitleCasePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import * as JSZip from 'jszip';
import { v4 as uuid } from 'uuid';

import { forkJoin, NEVER, Observable, ReplaySubject } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { Constants, ERROR_MESSAGES, FileMimeTypeEnum, FILES_FOLDERS_TYPES, RegexHelper, SnackBarHelperComponent, ValidatorHelper } from '../../../../../../helpers';
import { Attachment, BoqFilter, ContactClient, Division, DocumentGeneratorParams, Email, ExportDocument, Project } from '../../../../../../interfaces';
import { AuthUserModel, UserModel } from '../../../../../../models';
import { AttachmentService } from '../../../../../../services/attachment.service';
import { DocumentGeneratorService } from '../../../../../../services/document-generator.service';
import { EmailService } from '../../../../../../services/email.service';
import { FileUploadService } from '../../../../../../services/file-uploader.service';
import { ModalService } from '../../../../../../services/modal.service';
import { RolePermissionsService } from '../../../../../../services/role-permissions.service';
import { S3Service } from '../../../../../../services/s3.service';
import { AttachmentsTabTreeNode } from '../../../../project-details/attachments-tab/common/attachments-tab.interface';
import { EmailDialogData, EmailResponse, ExportPreview, NodeChildAttachment, NodeChildTree } from '../../../common/export.interfaces';
import { text } from './text.constants';

@Injectable()
export class ExportTabsService {
	public readonly text: typeof text = text;
	public refreshSub: ReplaySubject<void> = new ReplaySubject();
	public emailSubject: ReplaySubject<EmailDialogData> = new ReplaySubject();

	constructor(
		private attachmentService: AttachmentService,
		private documentGenerator: DocumentGeneratorService,
		private emailService: EmailService,
		private fileUploadService: FileUploadService,
		private modalService: ModalService,
		private rolesPermissions: RolePermissionsService,
		private s3Service: S3Service,
		private snack: SnackBarHelperComponent,
		private titleCasePipe: TitleCasePipe
	) {}

	public passEmailSubject(ccUsers: UserModel[] | ContactClient[], email: Email, project: Project, users: Array<UserModel>): void {
		this.emailSubject.next({ ccUsers, email, project, users });
	}

	public sortAttachments(attachments: Array<Attachment>, title: string): Array<Attachment> {
		return attachments
			.filter(attachment => attachment.category.title === title)
			.sort((a: Attachment, b: Attachment) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
	}

	public openNewWindow(url: string, target: string): void {
		const newWindow: Window = window.open(url, target);

		try {
			newWindow.focus();
		} catch {
			this.snack.snackInformation(Constants.PLEASE_ALLOW_POPUP);
		}
	}

	public addNewAttachment(newAttachment: Attachment, title: string, project: Project, newDocFile: File): Observable<Attachment> {
		// Upload File, then add new Attachment
		const categoryName: string = RegexHelper.createAlphanumericalWithSpecialLength(title, 16);
		const path: string = `projects/${project.id}/attachments/${categoryName}_${uuid()}_${newDocFile.name}`;
		let fileUpload: Observable<string>;
		// If this is a word document, sanitise it before uploading
		if (newDocFile.type === FileMimeTypeEnum.doc || newDocFile.type === FileMimeTypeEnum.docx) {
			fileUpload = this.documentGenerator.uploadAndSanitiseDocument(newDocFile, path);
		} else {
			fileUpload = this.s3Service.uploadFile(newDocFile, path, newDocFile.type);
		}

		return fileUpload.pipe(
			switchMap((returnKey: string) => {
				newAttachment.key = returnKey;
				newAttachment.uploadedAt = new Date();
				newAttachment.project = project.id;

				return this.attachmentService.addAttachmentFromFolder(newAttachment, project.id, title).pipe(
					tap(() => {
						this.fileUploadService.cancelUpload();
						newAttachment = {
							name: undefined,
							category: undefined,
							key: undefined,
							revision: 1,
							uploadedAt: undefined,
							isGenerated: false,
							project: project.id,
							isActive: true,
						};
						this.refresh();
					})
				);
			})
		);
	}

	public toggleAttachmentActive(attachment: Attachment, projectId: string) {
		return this.attachmentService.editActive(attachment.id, !attachment.isActive, projectId).pipe(
			tap(() => {
				this.refresh();
				this.modalService.closeModal();
			})
		);
	}

	public refresh(): void {
		this.refreshSub.next();
	}

	public getZipName(archive: Array<Attachment>, project: Project): string {
		return archive.length > 1 ? project.reference + ' - ' + project.name + '.zip' : project.name + ' - ' + project.clients[0].contact.name + ' - ' + archive[0].name + '.zip';
	}

	public sendEmail(filesFolderKey: string, attachmentKey: string, projectId: string, emailCon, endpoint: string, validators?): Observable<EmailResponse> {
		const errors: string = ValidatorHelper.checkErrors(validators);
		if (errors) {
			this.snack.snackError(errors);
			return;
		} else {
			const htmlFormatted: string = emailCon.body.replace(/(\r\n|\n|\r)/gm, '<br>');

			const body: object = {
				subject: emailCon.subject,
				body: htmlFormatted,
				sentTo: emailCon.sentTo,
				project: projectId,
				filesFolderKey: filesFolderKey,
				attachmentKey: attachmentKey,
				replyTo: emailCon.replyTo,
				ccEmails: emailCon.ccEmails,
			};
			return this.emailService.sendEmail(body, endpoint);
		}
	}

	public canSendExportEmail(currentUser: AuthUserModel, project: Project): boolean {
		// Determine if project status is before 'Quote Approved'
		const quoteApprovedOrdinal: number = Constants.PROJECT_STATUS.quoteApproved.ordinal;
		const isBeforeQuoteApproved: boolean = Constants.PROJECT_STATUS[project.status].ordinal < quoteApprovedOrdinal;

		if (isBeforeQuoteApproved === true) {
			// User is a trainee and project status is before 'Quote Approved'
			if (currentUser.role === Constants.USER_ROLES.trainee.title) {
				return false;
			}

			// Project net totals between $100K and $1M and user is not team leader or admin
			if (project.netTotalChargeOut > 100000 && project.netTotalChargeOut <= 1000000 && !this.rolesPermissions.isTeamLeaderAndAbove()) {
				return false;
			}

			// Project net totals > $1M, user not admin, status before 'Quote Approved'
			if (project.netTotalChargeOut > 1000000 && !this.rolesPermissions.isAdmin()) {
				return false;
			}
		}

		return true;
	}

	public generate(
		documentsList: Array<ExportDocument>,
		boqFilter: BoqFilter,
		currentUser: AuthUserModel,
		project: Project,
		selectedClients: Array<ContactClient>,
		title: string,
		selectedGroupByField: string,
		selectedDivisionFilter: Division,
		selectedFilesForGenerator: Array<ExportDocument>,
		download?: boolean,
		variationId?: string,
	): Observable<Array<ExportPreview>> {
		return forkJoin(
			selectedClients.map((client: ContactClient) => {
				// Check the index of client can be found
				const indexOfClient: number = project.clients.findIndex((value: ContactClient) => value.id === client?.id);
				if (!client || indexOfClient === -1) {
					this.snack.snackError(ERROR_MESSAGES.invalidClient);
					return;
				}
				let documentGeneratorParams: DocumentGeneratorParams = {
					projectId: project.id,
					templates: selectedFilesForGenerator,
					clientIndex: indexOfClient,
					attachmentTypeName: title,
					skipPdfConversion: !download,
				};

				if (title === FILES_FOLDERS_TYPES.quote.name) {
					documentGeneratorParams.groupByField = selectedGroupByField;
				} else if (title === FILES_FOLDERS_TYPES.boq.name) {
					documentGeneratorParams.exportFilter = boqFilter;
				} else if (title === FILES_FOLDERS_TYPES.variation_quote.name) {
					if (!variationId) {
						this.snack.snackError(ERROR_MESSAGES.pleaseSelectVariation);
						return;
					}
					documentGeneratorParams.variationId = variationId;
				} else if (title === FILES_FOLDERS_TYPES.site_folder.name || title === FILES_FOLDERS_TYPES.office_folder.name) {
					documentGeneratorParams.variationId = variationId;
				}
				return this.documentGenerator.generateDocument(documentGeneratorParams).pipe(map((result: ExportPreview) => ({ ...result, client, generationParams: documentGeneratorParams })));
			}),
		).pipe(shareReplay());
	}

	public retrieveNodeAttachments(nodes: AttachmentsTabTreeNode[], observables: Array<Observable<NodeChildTree>>, project: Project): Observable<Blob> {
		const zip = new JSZip(); // final zip
		// "Export all" zip files: {Project reference} - {Project name}
		// Folder produced zip files: {Project name} - {Client name} - {Folder name}
		const zipName =
			nodes.length > 1 ? project.reference + ' - ' + project.name + '.zip' : project.name + ' - ' + project.clients[0].contact.name + ' - ' + nodes[0].name + '.zip';

		return forkJoin(observables).pipe(
			tap((blobs: Array<NodeChildTree>) => {
				blobs.forEach((blob: NodeChildTree) => {
					let subfolder;
					if (blob.nodeChild.isActive) {
						// All folders besides superseded will be active and just use their respective folder names
						subfolder = zip.folder(blob.nodeChild.category.title);
					} else if (blob.nodeChild.category) {
						// If an inactive blob has a category, it is a file inside superseded/blob.nodeChild.category
						subfolder = zip.folder(this.titleCasePipe.transform(this.text.superseded)).folder(blob.nodeChild.category.title);
					} else {
						// If an inactive blob has no category, just put it in the superseded folder
						subfolder = zip.folder(this.titleCasePipe.transform(this.text.superseded));
					}
					const existingFileNames = new Set();
					const file = blob.nodeChild;
					// Generated files will have /generated/ in their path. Other files will have /attachments/ in their path
					const nameList = file.isGenerated ? file.key.split('/generated/') : file.key.split('/attachments/');
					if (nameList.length > 1) {
						let s3KeyFileExtension = '';
						for (let i = file.key.length - 1; i >= 0; i--) {
							if (file.key[i] === '.') {
								s3KeyFileExtension = file.key.substr(i);
								break;
							}
						}
						if (s3KeyFileExtension) {
							let fileNameSuffix = 0;
							let finalFileName = file.name.replace(/[/\\?%*:|"<>]/g, '_').replace(s3KeyFileExtension, '') + s3KeyFileExtension;
							while (existingFileNames.has(finalFileName)) {
								fileNameSuffix++;
								finalFileName = file.name.replace(s3KeyFileExtension, '') + ` ${fileNameSuffix}` + s3KeyFileExtension;
							}
							existingFileNames.add(finalFileName);
							subfolder.file(finalFileName, blob.blob);
						} else {
							subfolder.file(nameList[1], blob.blob);
						}
					}
				});
			}),
			switchMap(() => zip.generateAsync({ type: 'blob' })),
			catchError(error => {
				this.snack.snackError(error);
				return NEVER;
			}),
			tap((archive: Blob) => {
				const zipFile = document.createElement('a');
				zipFile.href = window.URL.createObjectURL(archive);
				zipFile.download = zipName;
				document.body.appendChild(zipFile);
				zipFile.click();
				zipFile.remove();
			})
		);
	}

	public retrieveAttachments(observables: Array<Observable<NodeChildAttachment>>, archive: Array<Attachment>, project: Project): Observable<Blob> {
		// TODO consolidate
		const zip: typeof JSZip = new JSZip(); // final zip
		// "Export all" zip files: {Project reference} - {Project name}
		// Folder produced zip files: {Project name} - {Client name} - {Folder name}
		const zipName: string = this.getZipName(archive, project);
		return forkJoin(observables).pipe(
			tap((blobs: Array<NodeChildAttachment>) => {
				const existingFileNames: Set<string> = new Set();
				blobs.forEach((blob: NodeChildAttachment) => {
					let subfolder: JSZip;
					if (blob.nodeChild.isActive) {
						// All folders besides superseded will be active and just use their respective folder names
						subfolder = zip.folder(blob.nodeChild.category.title);
					} else if (blob.nodeChild.category) {
						// If an inactive blob has a category, it is a file inside superseded/blob.nodeChild.category
						subfolder = zip.folder(this.titleCasePipe.transform(this.text.superseded)).folder(blob.nodeChild.category.title);
					} else {
						// If an inactive blob has no category, just put it in the superseded folder
						subfolder = zip.folder(this.titleCasePipe.transform(this.text.superseded));
					}
					const file: Attachment = blob.nodeChild;
					// Generated files will have /generated/ in their path. Other files will have /attachments/ in their path
					const nameList: Array<string> = file.isGenerated ? file.key.split('/generated/') : file.key.split('/attachments/');
					if (nameList.length > 1) {
						let s3KeyFileExtension: string = '';
						for (let i: number = file.key.length - 1; i >= 0; i--) {
							if (file.key[i] === '.') {
								s3KeyFileExtension = file.key.substr(i);
								break;
							}
						}
						if (s3KeyFileExtension) {
							let fileNameSuffix: number = 0;
							let finalFileName: string = file.name.replace(/[/\\?%*:|"<>]/g, '_').replace(s3KeyFileExtension, '') + s3KeyFileExtension;
							while (existingFileNames.has(finalFileName)) {
								fileNameSuffix++;
								finalFileName = file.name.replace(s3KeyFileExtension, '') + ` ${fileNameSuffix}` + s3KeyFileExtension;
							}
							existingFileNames.add(finalFileName);
							subfolder.file(finalFileName, blob.blob);
						} else {
							subfolder.file(nameList[1], blob.blob);
						}
					}
				});
			}),
			switchMap(() => zip.generateAsync({ type: 'blob' })),
			catchError(error => {
				this.snack.snackError(error);
				return NEVER;
			}),
			tap((blob: Blob) => {
				const zipFile = document.createElement('a');
				zipFile.href = window.URL.createObjectURL(blob);
				zipFile.download = zipName;
				document.body.appendChild(zipFile);
				zipFile.click();
				zipFile.remove();
			})
		);
	}

	/**
	 * Creates a copy of the attachment in the "Tender Documents" folder
	 * 
	 * @param attachment 
	 * @returns 
	 */
	public cloneToTenderDocumentsFolder(attachment: Attachment) {
		return this.attachmentService.cloneToTenderFolder(attachment.id);
	}
}
